/**
 * ChapterNavSidebar — Phase 3
 * Lists chapters with status-aware rendering:
 *   complete   → clickable, shows word count
 *   generating → spinner + "Writing…"
 *   pending    → dimmed, not yet started
 *   error      → red tint
 * Free tier: only Chapter 1 accessible.
 */

import { cn } from '../../lib/utils';

export interface ChapterEntry {
  chapterNumber: number;
  title: string;
  status: 'complete' | 'generating' | 'pending' | 'error';
  wordCount?: number;
  isEdited?: boolean;
  editedWordCount?: number | null;
}

interface ChapterNavSidebarProps {
  chapters: ChapterEntry[];
  activeChapterNum: number;
  isPaidUser: boolean;
  onSelectChapter: (chapterNum: number) => void;
}

export function ChapterNavSidebar({
  chapters,
  activeChapterNum,
  isPaidUser,
  onSelectChapter,
}: ChapterNavSidebarProps) {
  return (
    <nav className="flex h-full flex-col border-r border-neutral-700 bg-neutral-900">
      {/* Header */}
      <div className="border-b border-neutral-700 px-4 py-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
          Chapters
        </h3>
      </div>

      {/* Chapter list */}
      <div className="flex-1 overflow-y-auto py-2">
        {chapters.length === 0 && (
          <p className="px-4 py-3 text-xs text-neutral-500">No chapters yet.</p>
        )}
        {chapters.map((ch) => {
          const isActive = ch.chapterNumber === activeChapterNum;
          const isLocked = !isPaidUser && ch.chapterNumber > 1;
          const isClickable = ch.status === 'complete' && !isLocked;

          return (
            <button
              key={ch.chapterNumber}
              type="button"
              onClick={() => isClickable && onSelectChapter(ch.chapterNumber)}
              disabled={!isClickable}
              className={cn(
                'group relative flex w-full flex-col items-start px-4 py-2.5 text-left transition-colors',
                isActive && ch.status === 'complete'
                  ? 'bg-white/10 text-white'
                  : ch.status === 'pending'
                  ? 'text-neutral-500 cursor-default'
                  : ch.status === 'generating'
                  ? 'text-neutral-300 cursor-default'
                  : ch.status === 'error'
                  ? 'text-neutral-400 cursor-default'
                  : 'text-neutral-300 hover:bg-white/5 hover:text-white',
                isLocked && ch.status === 'complete' && 'cursor-not-allowed',
              )}
            >
              <div className="flex w-full items-center justify-between gap-2">
                <span className="text-sm font-medium leading-snug line-clamp-1 flex-1">
                  {ch.chapterNumber}. {ch.title}
                </span>

                <div className="flex shrink-0 items-center gap-1">
                  {ch.isEdited && (
                    <span className="rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-400">
                      Edited
                    </span>
                  )}
                  {isLocked && ch.status === 'complete' && (
                    <span title="Upgrade to unlock" className="text-neutral-500 group-hover:text-neutral-300">
                      🔒
                    </span>
                  )}
                </div>
              </div>

              {/* Status sub-line */}
              {ch.status === 'complete' && (
                <span className="mt-0.5 text-[10px] text-neutral-400">
                  {(ch.isEdited && ch.editedWordCount != null
                    ? ch.editedWordCount
                    : ch.wordCount ?? 0
                  ).toLocaleString()}{' '}
                  words
                </span>
              )}
              {ch.status === 'generating' && (
                <span className="mt-0.5 flex items-center gap-1 text-[10px] text-blue-300">
                  <span className="h-2 w-2 animate-spin rounded-full border border-current border-t-transparent" />
                  Writing…
                </span>
              )}
              {ch.status === 'pending' && (
                <span className="mt-0.5 text-[10px] text-neutral-500">
                  Pending…
                </span>
              )}
              {ch.status === 'error' && (
                <span className="mt-0.5 text-[10px] text-red-400">
                  Failed
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
